import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, BACKEND_URL } from '@/App';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';import {
  Dumbbell, Newspaper, Trophy, Camera, Crown, Target, Users, Flame,
  Heart, MessageCircle, Send, ChevronDown, BarChart3, CalendarDays,
  Zap, UserPlus, TrendingUp, ArrowLeft, DoorOpen, Sparkles, Bike
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { memberColor, memberInitials } from '@/utils/memberColor';

const EVENT_CONFIG = {
  training_join:        { icon: Dumbbell,    color: 'text-cyan-400',    bg: 'bg-cyan-900/30',    label: 'Entreno' },
  training_leave:       { icon: DoorOpen,    color: 'text-rose-400',    bg: 'bg-rose-900/30',    label: 'Baja' },
  training_full:        { icon: Flame,       color: 'text-orange-400',  bg: 'bg-orange-900/30',  label: 'LLENO' },
  entrenamiento_fuerza: { icon: Dumbbell,    color: 'text-cyan-400',    bg: 'bg-cyan-900/30',    label: 'Fuerza' },
  record_broken:        { icon: Flame,       color: 'text-orange-400',  bg: 'bg-orange-900/30',  label: 'Record' },
  record_personal:      { icon: Trophy,      color: 'text-gold',        bg: 'bg-yellow-900/30',  label: 'RP' },
  hof_added:            { icon: Trophy,      color: 'text-gold',        bg: 'bg-yellow-900/30',  label: 'Hall of Fame' },
  badge_earned:         { icon: Trophy,      color: 'text-gold',        bg: 'bg-yellow-900/30',  label: 'Logro' },
  photo_upload:         { icon: Camera,      color: 'text-green-400',   bg: 'bg-green-900/30',   label: 'Foto' },
  news_created:         { icon: Newspaper,   color: 'text-purple-400',  bg: 'bg-purple-900/30',  label: 'Noticia' },
  new_pro:              { icon: Crown,       color: 'text-gold',        bg: 'bg-yellow-900/30',  label: 'Pro' },
  racha:                { icon: Zap,         color: 'text-amber-400',   bg: 'bg-amber-900/30',   label: 'Racha' },
  racha_entreno:        { icon: Zap,         color: 'text-amber-400',   bg: 'bg-amber-900/30',   label: 'Racha' },
  ranking:              { icon: TrendingUp,  color: 'text-emerald-400', bg: 'bg-emerald-900/30', label: 'Ranking' },
  nuevo_miembro:        { icon: UserPlus,    color: 'text-sky-400',     bg: 'bg-sky-900/30',     label: 'Bienvenida' },
  checkin:              { icon: Target,      color: 'text-indigo-400',  bg: 'bg-indigo-900/30',  label: 'Check-in' },
  checkin_completed:    { icon: Target,      color: 'text-indigo-400',  bg: 'bg-indigo-900/30',  label: 'Check-in' },
  reto_klan:            { icon: Bike,        color: 'text-emerald-400', bg: 'bg-emerald-900/30', label: 'Reto Klan' },
  resumen_semanal:      { icon: BarChart3,   color: 'text-gray-400',    bg: 'bg-gray-800/50',    label: 'Resumen' },
  estadistica:          { icon: BarChart3,   color: 'text-gray-400',    bg: 'bg-gray-800/50',    label: 'Stats' },
  comunidad:            { icon: Users,       color: 'text-gray-400',    bg: 'bg-gray-800/50',    label: 'Klan' },
  recordatorio:         { icon: CalendarDays,color: 'text-gray-400',    bg: 'bg-gray-800/50',    label: 'Info' },
  plan_grupal:          { icon: Target,      color: 'text-gold',        bg: 'bg-yellow-900/30',  label: 'Plan grupal' },
};

const FILTERS = [
  { key: 'todo', label: 'Todo' },
  { key: 'entreno', label: 'Entrenos' },
  { key: 'record', label: 'Records' },
  { key: 'logro', label: 'Logros' },
  { key: 'social', label: 'Social' },
];

// Refraseado emocional según tipo de evento. Recibe `item` y devuelve JSX o string.
function emotionalDetails(item) {
  const md = item.metadata || {};
  switch (item.type) {
    case 'training_join':
      return <>se ha apuntado al entreno de <b className="text-cyan-300">{md.tipo || ''}</b>{md.fecha ? ` del ${md.fecha}` : ''} 💪</>;
    case 'training_leave':
      return <>se ha borrado del entreno de <span className="text-rose-300">{md.tipo || ''}</span>{md.fecha ? ` del ${md.fecha}` : ''} 🚪</>;
    case 'training_full':
      return <>¡acaba de <b className="text-orange-300">LLENAR</b> el entreno de {md.tipo || ''}{md.fecha ? ` del ${md.fecha}` : ''}! 🔥🔥</>;
    case 'record_personal':
      return <>🏆 ha <b className="text-gold">batido su récord personal</b> en <span className="text-yellow-300">{md.entreno || ''}</span>: <b>{md.marca || ''}</b></>;
    case 'hof_added':
      return <>🏅 ha entrado en el Hall of Fame con <b>{md.marca || ''}</b> en <span className="text-yellow-300">{md.entreno || ''}</span></>;
    case 'entrenamiento_fuerza':
      return <>💪 ha completado una sesión de <b className="text-cyan-300">fuerza</b></>;
    case 'racha_entreno':
      return <>🔥 lleva <b className="text-amber-300">{md.semanas || ''} semanas</b> consecutivas entrenando</>;
    case 'nuevo_miembro':
      return <>✨ se ha unido al <b>Klan</b>. ¡Bienvenido/a!</>;
    case 'new_pro':
      return <>👑 es ahora <b className="text-gold">miembro PRO</b></>;
    case 'photo_upload':
      return <>📸 ha subido una foto nueva a la galería</>;
    default:
      return item.details;
  }
}

function timeAgo(ts) {
  try {
    return formatDistanceToNow(new Date(ts), { addSuffix: true, locale: es });
  } catch { return ''; }
}

// --- Comment Section ---
function CommentSection({ eventId, count, onCountChange }) {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const loadComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/feed/${eventId}/comments`);
      setComments(res.data);
    } catch { /* ignore */ }
  }, [eventId]);

  useEffect(() => { if (open) loadComments(); }, [open, loadComments]);

  const submit = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/feed/${eventId}/comment`, { texto: text });
      setComments(prev => [...prev, res.data]);
      setText('');
      onCountChange(1);
    } catch { /* ignore */ } finally { setSending(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="flex items-center gap-1 text-gray-400 hover:text-gray-200 transition-colors text-xs"
        data-testid={`btn-comments-${eventId}`}>
        <MessageCircle className="w-3.5 h-3.5" />
        <span>{count || 0}</span>
      </button>
    );
  }

  return (
    <div className="mt-3 space-y-2" data-testid={`comments-section-${eventId}`}>
      <button onClick={() => setOpen(false)} className="text-xs text-gray-400 hover:text-gray-300">Ocultar comentarios</button>
      <div className="space-y-2 max-h-40 overflow-y-auto">
        {comments.map(c => (
          <div key={c.id} className="flex gap-2 items-start">
            <div className="w-5 h-5 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0 mt-0.5">
              {c.user_avatar ? (
                <img src={`${BACKEND_URL}${c.user_avatar}`} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="text-[9px] text-gray-400">{(c.user_nombre || '?')[0]}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs"><span className="font-semibold text-white">{c.user_nombre}</span>{' '}
                <span className="text-gray-400">{c.texto}</span></p>
              <p className="text-[10px] text-gray-400">{timeAgo(c.created_at)}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 items-center">
        <Input value={text} onChange={e => setText(e.target.value)} placeholder="Escribe un comentario..."
          className="bg-gray-800/50 border-gray-700 text-white text-xs h-8 flex-1"
          onKeyDown={e => e.key === 'Enter' && submit()} data-testid={`comment-input-${eventId}`} />
        <button onClick={submit} disabled={sending || !text.trim()}
          className="text-gold hover:text-gold/80 disabled:text-gray-600 transition-colors" data-testid={`comment-send-${eventId}`}>
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

const REACTIONS = [
  { tipo: 'like', emoji: '❤️', label: 'Me gusta' },
  { tipo: 'fire', emoji: '🔥', label: 'Fuego' },
  { tipo: 'muscle', emoji: '💪', label: 'Bestia' },
  { tipo: 'lion', emoji: '🦁', label: 'Lobo MK' },
  { tipo: 'clap', emoji: '👏', label: 'Aplauso' },
];

// --- Avatar con anillo de color por miembro ---
function UserAvatar({ item, size = 44 }) {
  const cfg = EVENT_CONFIG[item.type] || EVENT_CONFIG.training_join;
  const Icon = cfg.icon;
  const color = memberColor(item.user_id);
  const ringColor = item.es_sistema ? '#4b5563' : color.ring;
  return (
    <div
      className="rounded-full flex items-center justify-center flex-shrink-0 relative"
      style={{
        width: size, height: size,
        background: 'rgba(20,20,20,0.6)',
        boxShadow: `0 0 0 2px ${ringColor}, 0 0 12px ${ringColor}55`,
      }}
    >
      {item.user_avatar ? (
        <img src={`${BACKEND_URL}${item.user_avatar}`} alt={item.user_name || ''} className="w-full h-full rounded-full object-cover" />
      ) : item.user_name ? (
        <span className="text-sm font-bold" style={{ color: color.text }}>{memberInitials(item.user_name)}</span>
      ) : (
        <Icon className={`w-5 h-5 ${cfg.color}`} />
      )}
      {/* Mini-badge del tipo de evento */}
      <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${cfg.bg} border border-gray-900 flex items-center justify-center`}
        title={cfg.label}>
        <Icon className={`w-3 h-3 ${cfg.color}`} />
      </div>
    </div>
  );
}

// --- Single Feed Event ---
function FeedEvent({ item, currentUserId }) {
  const cfg = EVENT_CONFIG[item.type] || EVENT_CONFIG.training_join;
  const isSistema = item.es_sistema;
  const isRecord = item.type === 'record_broken' || item.type === 'record_personal' || item.type === 'hof_added';
  const isFull = item.type === 'training_full';
  const color = memberColor(item.user_id);
  const [reacciones, setReacciones] = useState(() => {
    const base = { like: [], fire: [], muscle: [], lion: [], clap: [] };
    return { ...base, ...(item.reacciones || {}), like: (item.reacciones?.like) || item.likes || [] };
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [commCount, setCommCount] = useState(item.comentarios_count || 0);

  const myReactions = REACTIONS.filter(r => (reacciones[r.tipo] || []).some(x => x.user_id === currentUserId));
  const totalCount = REACTIONS.reduce((acc, r) => acc + (reacciones[r.tipo] || []).length, 0);

  const topReactions = REACTIONS
    .map(r => ({ ...r, count: (reacciones[r.tipo] || []).length }))
    .filter(r => r.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  const toggleReaccion = async (tipo) => {
    try {
      const res = await axios.post(`${API}/feed/${item.id}/reaccion`, { tipo });
      setReacciones(res.data.reacciones);
    } catch { /* ignore */ }
    setPickerOpen(false);
  };

  if (isSistema) {
    const Icon = cfg.icon;
    return (
      <div className="flex gap-3 items-start px-3 py-3 rounded-xl bg-gray-900/40 border border-dashed border-gray-800/60"
        data-testid={`feed-event-sistema-${item.id}`}>
        <div className="w-9 h-9 rounded-full bg-gray-800/60 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Icon className="w-4 h-4 text-gray-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-400">{item.details}</p>
          <p className="text-[10px] text-gray-400 mt-1">{timeAgo(item.timestamp)} &middot; {cfg.label}</p>
        </div>
      </div>
    );
  }

  const cardStyle = isRecord
    ? 'bg-gradient-to-br from-gold/10 to-yellow-900/5 border border-gold/30'
    : isFull
      ? 'bg-gradient-to-br from-orange-900/20 to-rose-900/10 border border-orange-500/30'
      : 'bg-gray-900/80 border border-gray-800';

  return (
    <div className={`px-3 py-3 rounded-xl transition-colors ${cardStyle}`}
      style={{ borderLeft: `3px solid ${color.ring}` }}
      data-testid={`feed-event-${item.id}`}>
      <div className="flex gap-3 items-start">
        <UserAvatar item={item} size={48} />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-100 leading-snug">
            <span className="font-bold" style={{ color: color.text }}>{item.user_name}</span>{' '}
            {emotionalDetails(item)}
          </p>
          {item.metadata?.image && (
            <div className="mt-2 rounded-lg overflow-hidden h-24 w-full max-w-[200px] border border-gray-700">
              <img src={`${BACKEND_URL}${item.metadata.image}`} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="flex items-center gap-3 mt-2 relative flex-wrap">
            <p className="text-[10px] text-gray-400">{timeAgo(item.timestamp)} &middot; {cfg.label}</p>

            {topReactions.length > 0 && (
              <div className="flex items-center gap-1" data-testid={`reactions-summary-${item.id}`}>
                {topReactions.map(r => {
                  const mine = (reacciones[r.tipo] || []).some(x => x.user_id === currentUserId);
                  return (
                    <button
                      key={r.tipo}
                      onClick={() => toggleReaccion(r.tipo)}
                      className={`flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full transition-colors ${mine ? 'bg-gold/20 border border-gold/40' : 'bg-gray-800/60 border border-gray-700 hover:bg-gray-700'}`}
                      data-testid={`btn-reaction-chip-${r.tipo}-${item.id}`}
                    >
                      <span className="text-sm leading-none">{r.emoji}</span>
                      <span className="text-[10px] text-gray-300">{r.count}</span>
                    </button>
                  );
                })}
                {totalCount > 0 && topReactions.length > 0 && (
                  <span className="text-[10px] text-gray-500 ml-1">{totalCount}</span>
                )}
              </div>
            )}

            <button
              onClick={() => setPickerOpen(o => !o)}
              className={`flex items-center gap-1 text-xs transition-colors p-1.5 -m-1.5 rounded-lg ${myReactions.length > 0 ? 'text-gold' : 'text-gray-400 hover:text-gold'}`}
              data-testid={`btn-react-${item.id}`}
              title={myReactions.length > 0 ? `Has reaccionado con ${myReactions.map(r => r.emoji).join('')}` : 'Reaccionar'}
            >
              {myReactions.length > 0 ? (
                <span className="text-base leading-none">{myReactions.map(r => r.emoji).join('')}</span>
              ) : (
                <>
                  <Heart className="w-4 h-4" />
                  <span className="text-[10px]">+</span>
                </>
              )}
            </button>

            {pickerOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setPickerOpen(false)} />
                <div className="absolute bottom-full mb-2 left-0 z-30 bg-gray-900 border border-gray-700 rounded-full px-2 py-1.5 flex items-center gap-1 shadow-2xl" data-testid={`reaction-picker-${item.id}`}>
                  {REACTIONS.map(r => {
                    const mine = (reacciones[r.tipo] || []).some(x => x.user_id === currentUserId);
                    return (
                      <button
                        key={r.tipo}
                        onClick={() => toggleReaccion(r.tipo)}
                        className={`text-xl hover:scale-125 transition-transform p-1 ${mine ? 'opacity-100 ring-2 ring-gold rounded-full' : 'opacity-80 hover:opacity-100'}`}
                        title={r.label}
                        data-testid={`btn-react-pick-${r.tipo}-${item.id}`}
                      >
                        {r.emoji}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            <CommentSection eventId={item.id} count={commCount} onCountChange={delta => setCommCount(c => c + delta)} />
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Top destacados (highlights) ---
function Highlights({ items }) {
  // Filtra eventos no-sistema con al menos 1 reacción o un evento "potente"
  const potentes = ['record_personal', 'record_broken', 'hof_added', 'training_full', 'racha_entreno', 'new_pro'];
  const candidatos = items
    .filter(it => !it.es_sistema)
    .map(it => {
      const reax = it.reacciones || {};
      const total = ['like','fire','muscle','lion','clap'].reduce((a, k) => a + (reax[k]?.length || 0), 0);
      const bonus = potentes.includes(it.type) ? 3 : 0;
      return { ...it, _score: total + bonus };
    })
    .filter(it => it._score > 0)
    .sort((a, b) => b._score - a._score)
    .slice(0, 6);

  if (candidatos.length === 0) return null;

  return (
    <div className="mb-3" data-testid="muro-highlights">
      <div className="flex items-center gap-2 mb-2 px-1">
        <Sparkles className="w-4 h-4 text-gold" />
        <span className="text-xs uppercase tracking-wider text-gold font-semibold">Top destacados</span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar -mx-1 px-1">
        {candidatos.map(it => {
          const cfg = EVENT_CONFIG[it.type] || EVENT_CONFIG.training_join;
          const color = memberColor(it.user_id);
          return (
            <div key={it.id}
              className="flex-shrink-0 w-40 rounded-xl bg-gray-900/60 border border-gray-800 p-3 hover:border-gold/40 transition-colors"
              style={{ borderTop: `2px solid ${color.ring}` }}
              data-testid={`highlight-${it.id}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <UserAvatar item={it} size={32} />
                <div className="min-w-0">
                  <p className="text-xs font-bold truncate" style={{ color: color.text }}>{it.user_name}</p>
                  <p className="text-[9px] text-gray-400">{cfg.label}</p>
                </div>
              </div>
              <p className="text-[11px] text-gray-200 line-clamp-2 leading-snug">{emotionalDetails(it)}</p>
              <p className="text-[9px] text-gray-500 mt-1">{timeAgo(it.timestamp)}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Day Group Header ---
function DayHeader({ label }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="h-px flex-1 bg-gray-800" />
      <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</span>
      <div className="h-px flex-1 bg-gray-800" />
    </div>
  );
}

function groupByDay(items) {
  const now = new Date();
  const today = now.toDateString();
  const yesterday = new Date(now - 86400000).toDateString();
  const groups = {};
  items.forEach(item => {
    const d = new Date(item.timestamp);
    let label;
    if (d.toDateString() === today) label = 'Hoy';
    else if (d.toDateString() === yesterday) label = 'Ayer';
    else {
      const diff = Math.floor((now - d) / 86400000);
      label = diff <= 7 ? 'Esta semana' : 'Anteriores';
    }
    if (!groups[label]) groups[label] = [];
    groups[label].push(item);
  });
  return groups;
}

// --- Main Feed Page ---
export default function MuroKlan() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todo');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef(null);

  const fetchFeed = useCallback(async (reset = false) => {
    try {
      const o = reset ? 0 : offset;
      const res = await axios.get(`${API}/feed?limit=30&offset=${o}&categoria=${filter}`);
      if (reset) {
        setItems(res.data);
        setOffset(30);
      } else {
        setItems(prev => [...prev, ...res.data]);
        setOffset(o + 30);
      }
      setHasMore(res.data.length >= 30);
    } catch (err) {
      console.error('Error fetching feed:', err);
    } finally { setLoading(false); }
  }, [filter, offset]);

  useEffect(() => {
    setLoading(true);
    setItems([]);
    setOffset(0);
    setHasMore(true);
    fetchFeed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    const interval = setInterval(() => fetchFeed(true), 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const groups = groupByDay(items);
  const groupOrder = ['Hoy', 'Ayer', 'Esta semana', 'Anteriores'];

  // Solo mostrar highlights cuando estamos en filtro "todo"
  const showHighlights = filter === 'todo';

  return (
    <div className="min-h-screen bg-dark-mk" data-testid="muro-klan-page">
      <div className="sticky top-0 z-30 bg-dark-mk/90 backdrop-blur-md border-b border-gray-800/50 px-4 pt-4 pb-3">
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-gray-300 hover:text-gold transition-colors text-sm"
            data-testid="muro-back-btn"
            aria-label="Volver al calendario"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Volver</span>
          </button>
          <h1 className="text-xl font-bold text-white flex items-center gap-2 flex-1">
            <Users className="w-5 h-5 text-gold" /> Muro del Klan
          </h1>
          <button
            onClick={() => navigate('/retos')}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-900/30 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-900/50 transition-colors text-xs font-medium"
            data-testid="muro-retos-btn"
          >
            <Bike className="w-3.5 h-3.5" />
            <span>Retos del Klan</span>
          </button>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar" data-testid="feed-filters">
          {FILTERS.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                filter === f.key ? 'bg-gold text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`} data-testid={`filter-${f.key}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pb-24 max-w-lg mx-auto pt-3" ref={containerRef}>
        {loading && items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-400 text-sm">Cargando actividad...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
            <Users className="w-10 h-10" />
            <p className="text-sm">No hay actividad reciente</p>
          </div>
        ) : (
          <>
            {showHighlights && <Highlights items={items} />}
            {groupOrder.map(label => {
              const group = groups[label];
              if (!group || group.length === 0) return null;
              return (
                <div key={label}>
                  <DayHeader label={label} />
                  <div className="space-y-3">
                    {group.map(item => (
                      <FeedEvent key={item.id} item={item} currentUserId={user?.id} />
                    ))}
                  </div>
                </div>
              );
            })}
            {hasMore && (
              <button onClick={() => fetchFeed(false)}
                className="w-full py-3 mt-4 text-sm text-gray-400 hover:text-gray-200 flex items-center justify-center gap-1 transition-colors"
                data-testid="btn-load-more">
                <ChevronDown className="w-4 h-4" /> Cargar mas
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
